import {toast} from '@wandb/weave/common/components/elements/Toast';
import React, {useCallback, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button, Checkbox, Icon, Modal} from 'semantic-ui-react';

import {envIsLocal} from '../config';
import {
  useDeleteInviteMutation,
  useRemoveUserFromOrganizationByIdMutation,
} from '../generated/graphql';
import {MemberData} from '../graphql/teamQuery';
import {OrganizationMemberFragment} from '../pages/Billing/BillingPage';
import {useViewer} from '../state/viewer/hooks';
import {propagateErrorsContext} from '../util/errors';
import {checkIsServiceAccount} from '../util/serviceAccounts';
import * as S from './DeleteMemberModal.styles';

enum DeleteMemberStep {
  ConfirmingDeletion,
  RemovedFromBoth,
  RemovedFromTeamContactOrgAdmin,
  RemovedFromTeamGoToEdit,
}

interface DeleteMemberModalProps {
  member?: MemberData;
  org?: any;
  entityName?: string;
  refetch?: () => void;
  onClose: () => void;
}

const DeleteMemberModal: React.FC<DeleteMemberModalProps> = ({
  member,
  org,
  entityName,
  refetch,
  onClose,
}) => {
  const [deleteFromOrg, setDeleteFromOrg] = useState<boolean>(false);
  const [step, setStep] = useState<DeleteMemberStep>(
    DeleteMemberStep.ConfirmingDeletion
  );
  const viewer = useViewer();

  const [deleteInvite] = useDeleteInviteMutation({
    context: propagateErrorsContext(),
  });
  const [removeUserFromOrgById] = useRemoveUserFromOrganizationByIdMutation({
    context: propagateErrorsContext(),
  });

  const isOrgAdmin: boolean = useMemo(
    () =>
      org?.members?.find(
        (m: OrganizationMemberFragment) =>
          m.username === viewer?.username && m.role === 'admin'
      ) ?? false,
    [org, viewer]
  );

  // If onPrem, hide this option (user should remain a member of the server org).
  // If the user being removed is a service account (which doesn't take
  // up a seat), hide this option.
  // this intentionally doesn't include irOrgAdmin checking because we want
  // to show the checkbox to non org admin so they are aware of removing from org for seat
  const showRemoveFromOrg = useMemo(
    () => !envIsLocal && !checkIsServiceAccount(member?.accountType),
    [member]
  );

  const removeUser = useCallback(async () => {
    if (!member) {
      return;
    }

    try {
      const deleteInviteResult = await deleteInvite({
        variables: {
          id: member.id,
          entityName,
        },
      });

      if (deleteInviteResult.errors != null) {
        toast(
          `Oops, something went wrong with removing ${member.username} from this team`
        );
        onClose();
        return;
      }

      if (!showRemoveFromOrg) {
        onClose();
        // make sure we refresh when deleting a service account
        refetch?.();
        return;
      }

      // if deleting from org is possible, there will be 3 situations
      // 1. [RemovedFromBoth] successfully removed from team and org
      // 2. [RemovedFromTeamContactOrgAdmin] removed from team but not org admin so cannot remove from org
      // 3. [RemovedFromTeamGoToEdit] remove from team but didn't try to or failed to remove from org
      if (!deleteFromOrg) {
        // situation 3
        setStep(DeleteMemberStep.RemovedFromTeamGoToEdit);
        return;
      }

      if (!isOrgAdmin) {
        // situation 2
        setStep(DeleteMemberStep.RemovedFromTeamContactOrgAdmin);
        return;
      }

      try {
        const removeUserFromOrgResult = await removeUserFromOrgById({
          variables: {
            userId: member.id,
            organizationId: org.id,
          },
        });
        if (removeUserFromOrgResult.errors != null) {
          // situation 3
          toast(
            `Oops, something went wrong with removing ${member.username} from organization ${org.name}`
          );
          setStep(DeleteMemberStep.RemovedFromTeamGoToEdit);
          return;
        }
      } catch (err) {
        // situation 3
        toast(
          `Oops, something went wrong with removing ${member.username} from organization ${org.name}`
        );
        setStep(DeleteMemberStep.RemovedFromTeamGoToEdit);
        return;
      }
      // situation 1
      setStep(DeleteMemberStep.RemovedFromBoth);
    } catch (err) {
      toast(
        `Oops, something went wrong with removing ${member.username} from team`
      );
      onClose();
    }
  }, [
    member,
    entityName,
    org,
    deleteInvite,
    removeUserFromOrgById,
    onClose,
    refetch,
    deleteFromOrg,
    isOrgAdmin,
    showRemoveFromOrg,
  ]);

  const modalHeader = useMemo(() => {
    switch (step) {
      case DeleteMemberStep.ConfirmingDeletion:
        return (
          <>
            <Icon name={'exclamation triangle'} /> Danger
          </>
        );
      case DeleteMemberStep.RemovedFromBoth:
        return `${member?.username} Removed from Team and Organization`;
      case DeleteMemberStep.RemovedFromTeamContactOrgAdmin:
      case DeleteMemberStep.RemovedFromTeamGoToEdit:
        return `${member?.username} Still in Organization`;
      default:
        return;
    }
  }, [step, member]);

  const modalContent = useMemo(() => {
    switch (step) {
      case DeleteMemberStep.ConfirmingDeletion:
        return (
          <>
            <p>
              Are you sure you want to remove{' '}
              {/* Note: service accounts just have the name field populated */}
              <S.Bold>
                {member?.username ?? member?.email ?? member?.name}
              </S.Bold>{' '}
              from <S.Bold>{entityName ?? 'this team'}</S.Bold>?
            </p>
            {showRemoveFromOrg && (
              <S.RemoveFromOrgSection>
                <Checkbox
                  checked={deleteFromOrg}
                  data-test="delete-from-org-checkbox"
                  onClick={() => {
                    setDeleteFromOrg(prev => !prev);
                  }}></Checkbox>
                <S.RemoveFromOrgContent>
                  Remove <S.Bold>{member?.username}</S.Bold> from your
                  organization <S.Bold>{org?.name}</S.Bold>
                </S.RemoveFromOrgContent>
              </S.RemoveFromOrgSection>
            )}
          </>
        );
      case DeleteMemberStep.RemovedFromBoth:
        return (
          <>
            You have successfully removed <S.Bold>{member?.username}</S.Bold>{' '}
            from your team <S.Bold>{entityName}</S.Bold> and organization{' '}
            <S.Bold>{org.name}</S.Bold>. Go to the Subscription Page to edit org
            members.
          </>
        );
      case DeleteMemberStep.RemovedFromTeamContactOrgAdmin:
        return (
          <>
            You have successfully removed <S.Bold>{member?.username}</S.Bold>{' '}
            from your team <S.Bold>{entityName}</S.Bold>, but they are still a
            member of your paid organization. Please contact your org admin to
            have them removed.
          </>
        );
      case DeleteMemberStep.RemovedFromTeamGoToEdit:
        return (
          <>
            You have successfully removed <S.Bold>{member?.username}</S.Bold>{' '}
            from your team <S.Bold>{entityName}</S.Bold>, but they are still a
            member of your paid organization. Go to the Subscription Page to
            edit org members.
          </>
        );
      default:
        return;
    }
  }, [step, member, org, entityName, deleteFromOrg, showRemoveFromOrg]);

  return (
    <Modal open onClose={onClose} size="small">
      <Modal.Header>{modalHeader}</Modal.Header>
      <Modal.Content>{modalContent}</Modal.Content>
      {step === DeleteMemberStep.ConfirmingDeletion ? (
        <Modal.Actions>
          <S.ButtonWrapper>
            <Button onClick={onClose} content="Cancel" />
            <Button onClick={removeUser} content="Yes" color="red" />
          </S.ButtonWrapper>
        </Modal.Actions>
      ) : (
        <Modal.Actions>
          <Button
            onClick={() => {
              // removed member from the team so the list gets refetched
              onClose();
              refetch?.();
            }}
            content="Close"
          />
        </Modal.Actions>
      )}
    </Modal>
  );
};

export default DeleteMemberModal;
